/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    AddOrderTemplateToCartComponent,
    ILinesAddedToCartDialogResources,
    IOrderTemplateNameDialogResources,
    RenameOrderTemplateComponent,
    OrderTemplateNameDialog
} from '@msdyn365-commerce/components';
import { IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import { ProductList } from '@msdyn365-commerce/retail-proxy';
import { OrderTemplate, Random } from '@msdyn365-commerce-modules/retail-actions';
import { Button, ITelemetryContent, Popover } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { useState } from 'react';
import { ShareOrderTemplateDialog, IShareOrderTemplateDialogResources } from './order-template-share-modal';
import { DeleteOrderTemplateDialog, IDeleteOrderTemplateDialogResources } from './order-template-delete-modal';

export interface IOrderTemplateActionsData {
    orderTemplate: OrderTemplate;
}

export interface IOrderTemplateActionsProps extends IComponentProps<IOrderTemplateActionsData> {
    context: ICoreContext;
    telemetryContent?: ITelemetryContent;
    handlers: {
        onRemoveTemplate(orderTemplate: ProductList | string): Promise<void> | void;
        onRenameTemplate(orderTemplate: ProductList): Promise<void>;
        onShareTemplate?(orderTemplate: ProductList): Promise<void>;
    };
    renameOrderTemplateDialogStrings: IOrderTemplateNameDialogResources;
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    isShared?: boolean;
    canManageOrderTemplates?: boolean;
    showOptionsLabel?: boolean;
    isB2bContact?: boolean;
    resources: {
        addTemplateToBagButtonText: string;
        renameTemplateButtonText: string;
        deleteTemplateButtonText: string;
        closeWindowButtonText: string;
        addToCartFailureMessage: string;
        addToCartSuccessMessage: string;
        addToCartProcessMessage: string;
        orderTemplateErrorMessage: string;
        linesAddedToCartDialogResources: ILinesAddedToCartDialogResources;
        templateShareButtonText?: string;
        templateUnshareButtonText?: string;
        ShareOrderTemplateDialog?: IShareOrderTemplateDialogResources;
        DeleteOrderTemplateDialog?: IDeleteOrderTemplateDialogResources;
    };
}

export interface IOrderTemplateActionsViewProps {
    addTemplateToBag: React.ReactNode;
    renameTemplate?: React.ReactNode;
    removeTemplate?: React.ReactNode;
    isMultipleCatalogsFeatureEnabled?: boolean;
    actionsSplitButton?: React.ReactNode;
}

export const canManageOrderTemplate = (
    productList: ProductList,
    context: ICoreContext,
    isShared?: boolean,
    canManageOrderTemplates?: boolean
): boolean => {
    // Keep in sync with order-template-list-actions.tsx::canManageOrderTemplate
    const isTemplateShared = Boolean(isShared || productList.IsShared);
    const userContactId = context?.request?.user?.contactPersonId;
    const isB2bContact = context?.request?.user?.isB2bContact;
    const isB2bContactOwner = !!productList.ContactPersonId && !!userContactId && productList.ContactPersonId === userContactId;
    const isTemplateOwner = !isB2bContact || isB2bContactOwner;

    return isTemplateOwner || (isTemplateShared && !!canManageOrderTemplates);
};

export const OrderTemplateActionsView = (props: IOrderTemplateActionsProps): IOrderTemplateActionsViewProps => {
    const { isMultipleCatalogFeatureEnabledInHq } = props;
    const canManage = canManageOrderTemplate(
        props.data.orderTemplate.productList,
        props.context,
        props.isShared,
        props.canManageOrderTemplates
    );

    return {
        addTemplateToBag: _getOrderTemplateAddToCartDialog(props),
        renameTemplate: canManage && !isMultipleCatalogFeatureEnabledInHq && _getOrderTemplateRenameControl(props),
        removeTemplate: canManage && !isMultipleCatalogFeatureEnabledInHq && <NonMultiCatalogDeleteControl {...props} />,
        actionsSplitButton: canManage && isMultipleCatalogFeatureEnabledInHq && <ActionsSplitButton {...props} showOptionsLabel={true} />
    };
};

const _removeOrderTemplate = (props: IOrderTemplateActionsProps): void => {
    const {
        data: { orderTemplate },
        handlers
    } = props;
    const { onRemoveTemplate } = handlers;

    if (onRemoveTemplate) {
        onRemoveTemplate(orderTemplate.productList);
    }
};

const NonMultiCatalogDeleteControl: React.FC<IOrderTemplateActionsProps> = (props: IOrderTemplateActionsProps) => {
    const [isDeleteOrderTemplateDialogOpen, setDeleteOrderTemplateDialogOpen] = React.useState(false);
    const buttonEventReference = React.useRef<HTMLButtonElement | null>(null);
    const {
        data: { orderTemplate },
        resources,
        context
    } = props;

    const onDeleteButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        buttonEventReference.current = event.target as HTMLButtonElement;

        if (resources.DeleteOrderTemplateDialog) {
            setDeleteOrderTemplateDialogOpen(true);
        } else {
            _removeOrderTemplate(props);
        }

        event.preventDefault();
    };

    const onOrderTemplateDeleteCancel = () => {
        context.telemetry.debug('Delete of template was canceled.');
        buttonEventReference.current?.focus();
    };

    return (
        <>
            {resources.DeleteOrderTemplateDialog && (
                <DeleteOrderTemplateDialog
                    modalOpen={isDeleteOrderTemplateDialogOpen}
                    setModalOpen={setDeleteOrderTemplateDialogOpen}
                    productList={orderTemplate.productList}
                    context={context}
                    id={props.id}
                    data={props.data}
                    typeName={props.typeName}
                    onSuccess={props.handlers.onRemoveTemplate}
                    onCancel={onOrderTemplateDeleteCancel}
                    resources={resources.DeleteOrderTemplateDialog}
                />
            )}
            <Button className='ms-order-template-action-bar__delete-template-button' onClick={onDeleteButtonClick}>
                {resources.deleteTemplateButtonText}
            </Button>
        </>
    );
};

const _getOrderTemplateAddToCartDialog = (props: IOrderTemplateActionsProps): React.ReactNode => {
    const {
        data: { orderTemplate },
        typeName,
        id,
        context,
        telemetryContent,
        resources,
        isMultipleCatalogFeatureEnabledInHq
    } = props;
    const {
        addTemplateToBagButtonText,
        closeWindowButtonText,
        addToCartFailureMessage,
        addToCartSuccessMessage,
        addToCartProcessMessage,
        orderTemplateErrorMessage,
        linesAddedToCartDialogResources
    } = resources;

    const dialogStrings = {
        closeWindowButtonText,
        addToCartFailureMessage,
        addToCartSuccessMessage,
        addToCartProcessMessage,
        orderTemplateErrorMessage,
        linesAddedToCartDialogResources
    };

    return (
        <AddOrderTemplateToCartComponent
            context={context}
            id={id}
            typeName={typeName}
            data={{ orderTemplate }}
            className=''
            addToCartText={addTemplateToBagButtonText}
            dialogStrings={dialogStrings}
            telemetryContent={telemetryContent}
            isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq}
        />
    );
};

const _getOrderTemplateRenameControl = (props: IOrderTemplateActionsProps): React.ReactNode => {
    const { renameOrderTemplateDialogStrings, resources, context, handlers } = props;
    return (
        <RenameOrderTemplateComponent
            onRenameTemplate={handlers.onRenameTemplate}
            className='msc-order-template-name-dialog'
            context={context}
            id={props.id}
            data={{ orderTemplate: props.data.orderTemplate.productList }}
            typeName={props.typeName}
            renameOrderTemplateDialogStrings={renameOrderTemplateDialogStrings}
            renameOrderTemplateButtonText={resources.renameTemplateButtonText}
            isMultipleCatalogFeatureEnabledInHq
        />
    );
};

export const ActionsSplitButton: React.FC<IOrderTemplateActionsProps> = (props: IOrderTemplateActionsProps) => {
    const [isStatementRequestAdminDropdownOpen, setIsStatementRequestAdminDropdownOpen] = useState(false);
    const [renameOrderTemplateDialogOpen, setOrderTemplateNameDialogOpen] = React.useState(false);
    const [isShareOrderTemplateDialogOpen, setShareOrderTemplateDialogOpen] = React.useState(false);
    const [isDeleteOrderTemplateDialogOpen, setDeleteOrderTemplateDialogOpen] = React.useState(false);

    const isShared = props.isShared ?? false;
    const showOptionsLabel = props.showOptionsLabel ?? false;

    const onOrderTemplateNameCancel = () => {
        context.telemetry.debug('Rename of template was canceled.');
        buttonEventReference.current?.focus();
    };

    const buttonEventReference = React.useRef<HTMLButtonElement | null>(null);

    const onRenameButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        buttonEventReference.current = event.target as HTMLButtonElement;
        setOrderTemplateNameDialogOpen(true);
        event.preventDefault();
    };

    const onShareButtonClcik = (event: React.MouseEvent<HTMLButtonElement>) => {
        buttonEventReference.current = event.target as HTMLButtonElement;
        setShareOrderTemplateDialogOpen(true);
        event.preventDefault();
    };

    const onDeleteButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        buttonEventReference.current = event.target as HTMLButtonElement;
        setDeleteOrderTemplateDialogOpen(true);
        event.preventDefault();
    };

    const onOrderTemplateDeleteCancel = () => {
        context.telemetry.debug('Delete of template was canceled.');
        buttonEventReference.current?.focus();
    };

    const onToggleDropdown1 = () => {
        setIsStatementRequestAdminDropdownOpen(!isStatementRequestAdminDropdownOpen);
    };
    const {
        data: { orderTemplate },
        resources,
        renameOrderTemplateDialogStrings,
        context
    } = props;

    const className = 'ms-order-template-action-bar';

    const popoverReference = React.createRef<HTMLButtonElement>();
    const renderModalPopup = (
        <OrderTemplateNameDialog
            existingOrderTemplate={orderTemplate.productList}
            isUpdateDialog
            className='msc-order-template-name-dialog'
            modalOpen={renameOrderTemplateDialogOpen}
            setModalOpen={setOrderTemplateNameDialogOpen}
            context={context}
            id={props.id}
            data={props.data}
            orderTemplateName={props.data.orderTemplate.productList.Name}
            typeName={props.typeName}
            dialogStrings={renameOrderTemplateDialogStrings}
            onCancel={onOrderTemplateNameCancel}
            onSuccess={props.handlers.onRenameTemplate}
        />
    );
    const requestStatementDropdownString = `request-statement-dropdown-id-${Random.Guid.generateGuid()}`;

    const renderButton = canManageOrderTemplate(orderTemplate.productList, props.context, props.isShared, props.canManageOrderTemplates);

    return renderButton ? (
        <div className={`${className}__dropdown`}>
            {renderModalPopup}
            {resources.ShareOrderTemplateDialog && props.handlers.onShareTemplate && (
                <ShareOrderTemplateDialog
                    className='msc-share-order-template-dialog'
                    modalOpen={isShareOrderTemplateDialogOpen}
                    setModalOpen={setShareOrderTemplateDialogOpen}
                    isShared={isShared}
                    productList={orderTemplate.productList}
                    context={context}
                    id={props.id}
                    data={props.data}
                    typeName={props.typeName}
                    onSuccess={props.handlers.onShareTemplate}
                    resources={resources.ShareOrderTemplateDialog}
                />
            )}
            {resources.DeleteOrderTemplateDialog && props.handlers.onRemoveTemplate && (
                <DeleteOrderTemplateDialog
                    modalOpen={isDeleteOrderTemplateDialogOpen}
                    setModalOpen={setDeleteOrderTemplateDialogOpen}
                    productList={orderTemplate.productList}
                    context={context}
                    id={props.id}
                    data={props.data}
                    typeName={props.typeName}
                    onSuccess={props.handlers.onRemoveTemplate}
                    onCancel={onOrderTemplateDeleteCancel}
                    resources={resources.DeleteOrderTemplateDialog}
                />
            )}
            {showOptionsLabel ? (
                <Button
                    className={
                        isStatementRequestAdminDropdownOpen
                            ? `${className}__action__requestStatement__toggleUp`
                            : `${className}__action__requestStatement__toggleDown`
                    }
                    onClick={onToggleDropdown1}
                    innerRef={popoverReference}
                    aria-haspopup='true'
                    aria-expanded={isStatementRequestAdminDropdownOpen}
                    id={requestStatementDropdownString}
                >
                    <span className={`${className}__action__requestStatement__buttonLabel`}>{'Options'}</span>
                </Button>
            ) : (
                <Button
                    className={
                        isStatementRequestAdminDropdownOpen
                            ? 'ms-order-template__list-options-toggle ms-order-template__list-options-toggle--open'
                            : 'ms-order-template__list-options-toggle'
                    }
                    onClick={onToggleDropdown1}
                    innerRef={popoverReference}
                    aria-haspopup='true'
                    aria-expanded={isStatementRequestAdminDropdownOpen}
                />
            )}
            <Popover
                aria-labelledby={requestStatementDropdownString}
                className={`${className}__action__requestStatement__dropdownVisible`}
                isOpen={isStatementRequestAdminDropdownOpen}
                placement='bottom'
                target={popoverReference}
                toggle={onToggleDropdown1}
                role='listbox'
                aria-hidden={!isStatementRequestAdminDropdownOpen}
            >
                <Button className={`${className}__action__requestStatement__dropdownOption rename`} onClick={onRenameButtonClick}>
                    {resources.renameTemplateButtonText}
                </Button>

                {(props.isB2bContact ?? false) && (props.canManageOrderTemplates ?? false) && (
                    <Button
                        className={`${className}__action__requestStatement__dropdownOption share`}
                        onClick={onShareButtonClcik}
                        role='option'
                    >
                        {isShared ? resources.templateUnshareButtonText : resources.templateShareButtonText}
                    </Button>
                )}

                <Button
                    className={`${className}__action__requestStatement__dropdownOption delete`}
                    onClick={onDeleteButtonClick}
                    role='option'
                >
                    {resources.deleteTemplateButtonText}
                </Button>
            </Popover>
        </div>
    ) : null;
};
